import { useEffect, useRef } from "react";
import { useAgentEvents, useUIEvents } from "@codenexus/react";
import type { AppChatEventMap } from "../chatTools/types";
import { useBatchGeneration } from "./BatchGenerationProvider";
import type { TrackedBatch } from "./types";

const terminal = new Set(["completed", "partial_failure", "failed", "cancelled", "blocked"]);

function originLabel(batch: TrackedBatch): string {
    switch (batch.handle?.origin) {
        case "product-hub-ui": return "Product Hub";
        case "feature-studio-ui": return "Feature Studio";
        default: return "FDE chat";
    }
}

function contextNote(batch: TrackedBatch): string {
    if (batch.phase === "blocked") {
        const gaps = batch.readiness?.products.flatMap(product =>
            product.missingInputs.map(input => `${product.productName}: ${input}`)
        ) || [];
        return `Batch generation was blocked by Context Mesh gaps. ${gaps.join(" ")}`.trim();
    }
    return `Batch ${batch.handle?.batchId || batch.trackingId} from ${originLabel(batch)} finished: ${batch.message}`;
}

export function BatchGenerationBridge(): null {
    const { registry } = useBatchGeneration();
    const bus = useUIEvents<AppChatEventMap>();
    const { subscribe: subscribeAgent } = useAgentEvents();
    const noted = useRef(new Set<string>());

    useEffect(() => registry.subscribe(batches => {
        batches.forEach(batch => {
            if (terminal.has(batch.phase) && !noted.current.has(batch.trackingId)) {
                noted.current.add(batch.trackingId);
                bus.pushContext({
                    batch_work_product_generation: {
                        batchId: batch.handle?.batchId,
                        origin: batch.handle?.origin || "chat",
                        phase: batch.phase,
                        counts: batch.counts
                    },
                    note: contextNote(batch)
                });
            }
        });
    }), [bus, registry]);
    useEffect(() => subscribeAgent(event => {
        if (event.type === "complete") {
            registry.failPendingChat("The FDE did not complete the batch tool sequence. Please try the request again.");
        } else if (event.type === "error" || event.type === "interrupted" || event.type === "skill_aborted") {
            registry.failPendingChat("The batch request was interrupted before generation started.");
        }
    }), [registry, subscribeAgent]);
    return null;
}
